
import { defineComponent, ref, onMounted, computed } from "vue";
import TableSummary from "../../components/table-summary/index.vue";
import ManualAssignModal from "../../components/modal-manual-assign/index.vue";
import Icons from "@/app/ui/components/icons/index.vue";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import {
  RequestListSummary,
  ListSummaryItem,
} from "@/domain/entities/PickupDashboardSummary";

interface SelectedSellerData {
  id: string;
  sellerName: string;
  sellerPhone: string;
  packageCount: number;
  koli: number;
  weight: number;
}
export default defineComponent({
  name: "PickupDashboardMonitoringCRRNFD",

  components: {
    TableSummary,
    Icons,
    ManualAssignModal,
  },

  setup() {
    const page = ref(1);
    const limit = ref(20);

    const sortKey = ref<string | null>(null);
    const sortOrder = ref<"asc" | "desc" | null>(null);

    const showAssignModal = ref(false);
    const selectedSellersForAssign = ref<SelectedSellerData[]>([]);

    const columns = [
      {
        key: "no",
        label: "pickupDashboard.column.no",
        style: { width: "60px", textAlign: "center" }
      },
      {
        key: "senderName",
        label: "pickupDashboard.column.senderName",
        sortable: true
      },
      {
        key: "shipmentType",
        label: "pickupDashboard.column.shipmentType"
      },
      {
        key: "totalShipment",
        label: "pickupDashboard.column.totalShipment",
        sortable: true
      },
      {
        key: "senderAddress",
        label: "pickupDashboard.column.address",
        style: { width: "280px" }
      },
      {
        key: "action",
        label: "pickupDashboard.column.assign",
        style: { width: "80px", textAlign: "center" }
      }
    ];

    const waNumber = (phone: string) => String(phone || "").replace(/\D/g, "");

    const detailSummary = computed(
      () => PickupDashboardController.detailSummary
    );

    const pagination = computed(() => PickupDashboardController.pagination);

    const startIndex = computed(
      () => (pagination.value.page - 1) * pagination.value.limit
    );

    const isLoading = computed(
      () => PickupDashboardController.loadingDetailSummary
    );

    const camelToSnake = (value: string): string => {
      return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    };

    const fetchListSummary = async () => {
      PickupDashboardController.setFilter({
        status: "CRRNFD",
      });

      const filterResult = PickupDashboardController.currentFilter;
      const startDate = filterResult.startDate || "";
      const endDate = filterResult.endDate || "";

      await PickupDashboardController.getListSummary(
        new RequestListSummary({
          q: filterResult.q,
          startDate: startDate,
          endDate: endDate,
          status: filterResult.status,
          shipmentType: filterResult.shipmentType,
          sortBy: sortOrder.value?.toUpperCase() ?? filterResult.sortBy,
          orderBy: camelToSnake(sortKey.value ?? filterResult.orderBy),
          page: page.value,
          limit: limit.value,
        })
      );
    };

    const handleSort = async ({
      key,
      order,
    }: {
      key: string;
      order: "asc" | "desc";
    }) => {
      sortKey.value = key;
      sortOrder.value = order;

      await fetchListSummary();
    };

    const handleSelectAll = (selectedItems: ListSummaryItem[]) => {
      PickupDashboardController.setTransferPickup(selectedItems);
    };

    const onSelectRow = (selectedItems: ListSummaryItem[]) => {
      PickupDashboardController.setTransferPickup(selectedItems);
    };

    const onPageChange = async (newPage: number) => {
      page.value = newPage;
      await fetchListSummary();
    };

    const openAssignModal = (item: ListSummaryItem) => {
      selectedSellersForAssign.value = [
        {
          id: item.shipmentId.toString(),
          sellerName: item.senderName,
          sellerPhone: item.senderPhone,
          packageCount: item.totalShipment,
          koli: item.totalQuantity,
          weight: item.totalWeight,
        },
      ];
      showAssignModal.value = true;
    };

    const handleAssignSuccess = async () => {
      // Reset selected sellers and close modal
      selectedSellersForAssign.value = [];
      showAssignModal.value = false;

      // Refresh list
      await fetchListSummary();
    };

    const fmtQty = (val: number | string) =>
      formatByCountry(Number(val || 0), { type: "quantity" });

    const mapsHref = (item: any) => {
      if (item.lat && item.lon) {
        return `https://www.google.com/maps?q=${item.lat},${item.lon}`;
      }
      const q = encodeURIComponent(item.sender_address || "");
      return `https://www.google.com/maps/search/?api=1&query=${q}`;
    };

    onMounted(async () => {
      await fetchListSummary();
    });

    return {
      columns,
      sortKey,
      sortOrder,
      detailSummary,
      pagination,
      startIndex,
      isLoading,
      mapsHref,
      fmtQty,
      handleSort,
      handleSelectAll,
      onSelectRow,
      onPageChange,
      showAssignModal,
      selectedSellersForAssign,
      openAssignModal,
      handleAssignSuccess,
      formatPhoneNumber,
      waNumber
    };
  }
});
